import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaFileExcel, FaSearch } from "react-icons/fa";

import { REACT_APP_API_URL } from "../../../../../env";

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getInitialFilters = () => {
  const today = new Date();
  return {
    fromDate: formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
    toDate: formatDateInput(today),
    hotelId: 0,
  };
};

const getHotelId = (hotel) =>
  hotel?.PropertyID ||
  hotel?.HotelID ||
  hotel?.intHotelId ||
  hotel?.hotelId ||  0;

const getHotelName = (hotel) =>  hotel?.UnitName ||  "";

const getRowsFromResponse = (payload) => {
  const candidates = [
    payload,
    payload?.data,
    payload?.Data
  ];

  const directArray = candidates.find(Array.isArray);
  if (directArray) return directArray;

  const nestedArray = candidates
    .filter((candidate) => candidate && typeof candidate === "object")
    .flatMap((candidate) => Object.values(candidate))
    .find(Array.isArray);

  return nestedArray || [];
};

const normalizeRow = (row) => {
  if (row && typeof row === "object" && !Array.isArray(row)) return row;
  return { Value: row };
};

const toTitle = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isValidDateValue = (value) => {
  if (!value || typeof value !== "string") return false;
  if (!/^\d{4}-\d{2}-\d{2}/.test(value) && !value.includes("T")) return false;

  return !Number.isNaN(new Date(value).getTime());
};

const isMoneyLikeKey = (key) =>
  /(amount|revenue|net|gross|tax|gst|charge|price|paid|refund|commission|discount|rent|fee|settlement)/i.test(
    key
  );

const formatNumber = (value) =>
  Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCellValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (isValidDateValue(value)) {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  if (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && isMoneyLikeKey(key)) {
      return formatNumber(numberValue);
    }
  }

  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

const getExportValue = (key, value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  if (isValidDateValue(value)) return formatCellValue(key, value);
  if (isMoneyLikeKey(key) && Number.isFinite(Number(value))) return Number(value);

  return value;
};

const getColumnHeader = (key) => {
  const title = toTitle(key);
  return isMoneyLikeKey(key) ? `${title} (INR)` : title;
};

const ConsolidatedSettlementSummaryTable = () => {
  const [filters, setFilters] = useState(getInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
  const [hotels, setHotels] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const columns = useMemo(() => {
    const keySet = new Set();
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => keySet.add(key));
    });

    return Array.from(keySet);
  }, [rows]);

  const fetchHotels = useCallback(async () => {
    try {
      const response = await axios.get(`${REACT_APP_API_URL}api/dashboard/admin/hotels`);
      const responseHotels = getRowsFromResponse(response.data)
        .map(normalizeRow)
        .map((hotel) => ({
          value: Number(getHotelId(hotel)) || 0,
          label: getHotelName(hotel) || `Hotel ${getHotelId(hotel)}`,
        }))
        .filter((hotel) => hotel.value || hotel.label)
        .sort((first, second) => first.label.localeCompare(second.label));

      setHotels(responseHotels);
    } catch (err) {
      console.error("Error fetching hotels:", err);
      setHotels([]);
    }
  }, []);

  const fetchReport = useCallback(async (reportFilters) => {
    if (!reportFilters.fromDate || !reportFilters.toDate) {
      setError("Please select From Date and To Date.");
      return;
    }

    if (new Date(reportFilters.fromDate) > new Date(reportFilters.toDate)) {
      setError("From Date cannot be after To Date.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/dashboard/admin/consolidated-payments-and-refunds`,
        {
          FromDate: reportFilters.fromDate,
          ToDate: reportFilters.toDate,
          HotelID: Number(reportFilters.hotelId) || null,
        },
        {
          params: {
            FromDate: reportFilters.fromDate,
            ToDate: reportFilters.toDate,
            HotelID: Number(reportFilters.hotelId) || null,
          },
        }
      );

      setRows(getRowsFromResponse(response.data).map(normalizeRow));
      setAppliedFilters(reportFilters);
    } catch (err) {
      console.error("Error fetching consolidated settlement summary:", err);
      setRows([]);
      setError("Failed to load consolidated settlement summary.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  useEffect(() => {
    const initialFilters = getInitialFilters();
    fetchReport(initialFilters);
  }, [fetchReport]);

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleSearch = () => {
    fetchReport(filters);
  };

  const handleExportExcel = () => {
    const exportRows = rows.map((row, index) => {
      const exportRow = { "#": index + 1 };
      columns.forEach((key) => {
        exportRow[getColumnHeader(key)] = getExportValue(key, row[key]);
      });
      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [{ wch: 8 }, ...columns.map(() => ({ wch: 22 }))];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Settlement Summary");
    XLSX.writeFile(
      workbook,
      `consolidated-settlement-summary-${appliedFilters.fromDate}-to-${appliedFilters.toDate}.xlsx`
    );
  };

  return (
    <div>
      <div className="row g-3 mb-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.fromDate}
            max={filters.toDate || undefined}
            onChange={(event) => handleFilterChange("fromDate", event.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.toDate}
            min={filters.fromDate || undefined}
            onChange={(event) => handleFilterChange("toDate", event.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Hotel</label>
          <select
            className="form-select"
            value={filters.hotelId}
            onChange={(event) =>
              handleFilterChange("hotelId", Number(event.target.value) || 0)
            }
          >
            <option value="0">All Hotels</option>
            {hotels.map((hotel) => (
              <option key={`${hotel.value}-${hotel.label}`} value={hotel.value}>
                {hotel.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-1">
          <button
            type="button"
            className="btn btn-primary w-100"
            onClick={handleSearch}
            disabled={loading}
            title="Show report"
            aria-label="Show report"
          >
            <FaSearch aria-hidden="true" />
          </button>
        </div>
        <div className="col-md-1">
          <button
            type="button"
            className="btn btn-outline-success w-100"
            onClick={handleExportExcel}
            disabled={loading || rows.length === 0}
            title="Export Excel"
            aria-label="Export Excel"
          >
            <FaFileExcel aria-hidden="true" />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted small">
          Showing {rows.length} record{rows.length === 1 ? "" : "s"}
        </div>
        <div className="text-muted small">
          {appliedFilters.fromDate} to {appliedFilters.toDate}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th scope="col">#</th>
              {columns.map((key) => (
                <th key={key} scope="col" className="text-nowrap">
                  {getColumnHeader(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={Math.max(columns.length + 1, 1)} className="text-center py-4">
                  Loading consolidated settlement summary...
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{rowIndex + 1}</td>
                  {columns.map((key) => (
                    <td key={key} className="text-nowrap">
                      {formatCellValue(key, row[key])}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(columns.length + 1, 1)} className="text-center py-4">
                  No consolidated settlement records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsolidatedSettlementSummaryTable;
